import { useRef } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useIntersectionObserver } from '@react-hookz/web';
import { useRouter } from 'next/router';
import { ScrollLink } from 'react-scroll';

import { WidthConstraint } from '@/components/layout/Section';

import type {
  StickySubnavAnchor,
  StickySubnav as StickySubnavType,
} from '@/sanity/sanity.types';
import type { MouseEvent } from 'react';

export interface StickySubnavProps extends StickySubnavType {
  allBlocks?: StickySubnavAnchor[];
}

export const getFormattedAnchor = (item) => {
  return `${item.toLowerCase().replaceAll(' ', '-')}`;
};

const StickySubnavLink = ScrollLink(
  ({
    item,
    currentItem,
    navIsScrolled,
    onClick,
    ...props
  }: {
    item: any;
    currentItem: any;
    navIsScrolled: boolean;
    onClick: (event: MouseEvent<HTMLAnchorElement>) => void;
  }) => {
    const router = useRouter();
    const link = `#${getFormattedAnchor(item)}`;

    const triggerNavAndScroll = (event: MouseEvent<HTMLAnchorElement>) => {
      onClick(event);
      router.push(link, null, { scroll: false });
    };

    return (
      <NavLink
        isSticky={navIsScrolled}
        isActive={currentItem === item}
        key={item}
        target="_self"
        aria-label={`Navigate to ${item} section.`}
        href={link}
        onClick={triggerNavAndScroll}
        {...props}
      >
        {item}
      </NavLink>
    );
  },
);

export const StickySubnav = ({ allBlocks = [] }: StickySubnavProps) => {
  const beforeNavRef = useRef<HTMLDivElement>();
  const navRef = useRef<HTMLDivElement>();

  const anchorHeadings = allBlocks
    .filter((block) => Boolean(block?.stickySubnavAnchorData))
    .map((block) => block.stickySubnavAnchorData);

  const router = useRouter();
  const currentHash = router.asPath.split('#')[1];
  const defaultItem = anchorHeadings[0];

  const currentItem =
    anchorHeadings.find((item) => getFormattedAnchor(item) === currentHash) ??
    defaultItem;

  const navIntersection = useIntersectionObserver(beforeNavRef, {
    rootMargin: '-80px',
    threshold: [0],
  });

  const navIsScrolled = !navIntersection?.isIntersecting;

  return (
    <>
      <Detector ref={beforeNavRef} />
      <NavWrapper isSticky={navIsScrolled} ref={navRef}>
        <WidthConstraint style={{ width: '100%' }}>
          {anchorHeadings.map((item, ind) => (
            <StickySubnavLink
              key={ind}
              className="sticky-nav"
              item={item}
              currentItem={currentItem}
              navIsScrolled={navIsScrolled}
              to={getFormattedAnchor(item)}
              smooth
              offset={-110}
              duration={500}
            />
          ))}
        </WidthConstraint>
      </NavWrapper>
    </>
  );
};

const linkStyle = (isSticky: boolean, isActive: boolean) => {
  if (isActive && isSticky) {
    return css`
      color: var(--decorative-starlight);
      border-bottom: 6px solid var(--decorative-starlight);
      padding-bottom: 9px;
    `;
  }

  if (isActive) {
    return css`
      color: var(--decorative-ultraviolet);
      border-bottom: 6px solid var(--decorative-ultraviolet);
      padding-bottom: 9px;
    `;
  }

  if (isSticky) {
    return css`
      color: var(--text-inverse-strong);
    `;
  }

  return css`
    color: var(--text-strong);
  `;
};

const Detector = styled.div`
  position: relative;
  height: 1px;
`;

const NavWrapper = styled.nav<{ isSticky: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  position: sticky;
  transition: background-color 0.3s ease;
  z-index: 5;
  overflow-y: hidden;
  overflow-x: auto;
  white-space: nowrap;
  max-width: 100vw;
  top: var(--navbar-height);

  background-color: var(--background-subtle);
  border-bottom: 1px solid var(--text-action-dark);

  ${(props) =>
    props.isSticky &&
    css`
      background-color: var(--decorative-dark-ultraviolet);
      border-bottom: none;
    `}
`;

const NavLink = styled.a<{
  isSticky: boolean;
  isActive: boolean;
}>`
  text-decoration: none;
  font-size: 18px;
  gap: 10px;
  margin-right: 20px;

  ${(props) => linkStyle(props.isSticky, props.isActive)}
`;
